// src/services/performance.service.js
import { api } from './api';

class PerformanceService {
    /**
     * Get team performance data (for managers)
     */
    async getTeamPerformance() {
        try {
            const response = await api.get('/api/performance/team');
            return response.data;
        } catch (error) {
            console.error('Error fetching team performance:', error);
            throw error;
        }
    }

    /**
     * Get employee goals
     */
    async getEmployeeGoals(employeeId) {
        try {
            const response = await api.get(`/api/performance/goals/${employeeId}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching employee goals:', error);
            throw error;
        }
    }

    /**
     * Create a performance goal
     */
    async createGoal(goalData) {
        try {
            const response = await api.post('/api/performance/goals', goalData);
            return response.data;
        } catch (error) {
            console.error('Error creating goal:', error);
            throw error;
        }
    }

    /**
     * Update goal progress
     */
    async updateGoalProgress(goalId, progressData) {
        try {
            const response = await api.put(`/api/performance/goals/${goalId}`, progressData);
            return response.data;
        } catch (error) {
            console.error('Error updating goal progress:', error);
            throw error;
        }
    }

    /**
     * Get performance review details
     */
    async getReview(reviewId) {
        try {
            const response = await api.get(`/api/performance/review/${reviewId}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching review:', error);
            throw error;
        }
    }

    /**
     * Get rating categories
     */
    async getRatingCategories() {
        try {
            const response = await api.get('/api/performance/categories');
            return response.data;
        } catch (error) {
            console.error('Error fetching rating categories:', error);
            throw error;
        }
    }

    /**
     * Get employee reviews
     */
    async getEmployeeReviews(employeeId) {
        try {
            const response = await api.get(`/api/performance/reviews/${employeeId}`);
            return response.data.reviews || [];
        } catch (error) {
            console.error('Error fetching employee reviews:', error);
            throw error;
        }
    }

    /**
     * Create a new performance review
     */
    async createReview(reviewData) {
        try {
            const response = await api.post('/api/performance/reviews', reviewData);
            return response.data;
        } catch (error) {
            console.error('Error creating review:', error);
            throw error;
        }
    }

    /**
     * Update an existing performance review
     */
    async updateReview(reviewId, reviewData) {
        try {
            const response = await api.put(`/api/performance/reviews/${reviewId}`, reviewData);
            return response.data;
        } catch (error) {
            console.error('Error updating review:', error);
            throw error;
        }
    }

    /**
     * Get a specific review by ID
     */
    async getReviewById(reviewId) {
        try {
            const response = await api.get(`/api/performance/reviews/detail/${reviewId}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching review:', error);
            throw error;
        }
    }

    // ========== TEMPLATE MANAGEMENT METHODS ==========

    /**
     * Get all templates
     */
    async getTemplates() {
        try {
            const response = await api.get('/api/performance/templates');
            return response.data.templates || [];
        } catch (error) {
            console.error('Error fetching templates:', error);
            throw error;
        }
    }

    /**
     * Create a new template
     */
    async createTemplate(templateData) {
        try {
            const response = await api.post('/api/performance/templates', templateData);
            return response.data;
        } catch (error) {
            console.error('Error creating template:', error);
            throw error;
        }
    }

    /**
     * Update a template
     */
    async updateTemplate(templateId, templateData) {
        try {
            const response = await api.put(`/api/performance/templates/${templateId}`, templateData);
            return response.data;
        } catch (error) {
            console.error('Error updating template:', error);
            throw error;
        }
    }

    /**
     * Delete a template
     */
    async deleteTemplate(templateId) {
        try {
            const response = await api.delete(`/api/performance/templates/${templateId}`);
            return response.data;
        } catch (error) {
            console.error('Error deleting template:', error);
            throw error;
        }
    }

    /**
     * Assign template
     */
    async assignTemplate(assignmentData) {
        try {
            const response = await api.post('/api/performance/templates/assign', assignmentData);
            return response.data;
        } catch (error) {
            console.error('Error assigning template:', error);
            throw error;
        }
    }

    /**
     * Get template assignments
     */
    async getTemplateAssignments(templateId) {
        try {
            const response = await api.get(`/api/performance/templates/${templateId}/assignments`);
            return response.data.assignments || [];
        } catch (error) {
            console.error('Error fetching template assignments:', error);
            throw error;
        }
    }

    // ========== ADMIN METHODS ==========

    /**
     * Get all performance reviews (admin view with filters)
     */
    async getAllReviews(filters = {}) {
        try {
            const response = await api.get('/api/performance/admin/reviews', { params: filters });
            return response.data;
        } catch (error) {
            console.error('Error fetching all reviews:', error);
            throw error;
        }
    }

    // ========== EMPLOYEE SELF-REVIEW METHODS ==========

    /**
     * Get employee's own reviews (self-reviews and manager reviews)
     */
    async getMyReviews() {
        try {
            const response = await api.get('/api/performance/employee/reviews');
            return response.data;
        } catch (error) {
            console.error('Error fetching my reviews:', error);
            throw error;
        }
    }

    /**
     * Get employee's reporting managers
     */
    async getMyManagers() {
        try {
            const response = await api.get('/api/performance/employee/managers');
            return response.data.managers || [];
        } catch (error) {
            console.error('Error fetching my managers:', error);
            throw error;
        }
    }

    /**
     * Get template assigned to employee
     */
    async getMyTemplate() {
        try {
            const response = await api.get('/api/performance/employee/template');
            return response.data.template;
        } catch (error) {
            console.error('Error fetching my template:', error);
            throw error;
        }
    }

    /**
     * Get template assigned to a specific employee (for managers)
     */
    async getEmployeeTemplate(employeeId) {
        try {
            const response = await api.get(`/api/performance/employee/${employeeId}/template`);
            return response.data;
        } catch (error) {
            console.error('Error fetching employee template:', error);
            throw error;
        }
    }

    /**
     * Create self-review
     */
    async createSelfReview(reviewData) {
        try {
            const response = await api.post('/api/performance/employee/self-review', reviewData);
            return response.data;
        } catch (error) {
            console.error('Error creating self-review:', error);
            throw error;
        }
    }

    /**
     * Update self-review
     */
    async updateSelfReview(reviewId, reviewData) {
        try {
            const response = await api.put(`/api/performance/employee/self-review/${reviewId}`, reviewData);
            return response.data;
        } catch (error) {
            console.error('Error updating self-review:', error);
            throw error;
        }
    }

    /**
     * Delete self-review (drafts only)
     */
    async deleteSelfReview(reviewId) {
        try {
            const response = await api.delete(`/api/performance/employee/self-review/${reviewId}`);
            return response.data;
        } catch (error) {
            console.error('Error deleting self-review:', error);
            throw error;
        }
    }

    // ========== MANAGER REVIEW METHODS ==========

    /**
     * Get team's self-reviews (for managers to review)
     */
    async getTeamSelfReviews(filters = {}) {
        try {
            const response = await api.get('/api/performance/manager/team-self-reviews', { params: filters });
            return response.data.reviews || [];
        } catch (error) {
            console.error('Error fetching team self-reviews:', error);
            throw error;
        }
    }

    /**
     * Create manager review
     */
    async createManagerReview(reviewData) {
        try {
            const response = await api.post('/api/performance/manager/create-review', reviewData);
            return response.data;
        } catch (error) {
            console.error('Error creating manager review:', error);
            throw error;
        }
    }

    // ========== ADMIN REVIEW CONFIRMATION METHODS ==========

    /**
     * Get reviews pending admin confirmation
     */
    async getPendingReviews(filters = {}) {
        try {
            const response = await api.get('/api/performance/admin/pending-reviews', { params: filters });
            return response.data.reviews || [];
        } catch (error) {
            console.error('Error fetching pending reviews:', error);
            throw error;
        }
    }

    /**
     * Admin confirm/edit review
     */
    async adminConfirmReview(reviewId, confirmationData) {
        try {
            const response = await api.put(`/api/performance/admin/confirm-review/${reviewId}`, confirmationData);
            return response.data;
        } catch (error) {
            console.error('Error confirming review:', error);
            throw error;
        }
    }

    /**
     * Get activity log for a review
     */
    async getReviewActivityLog(reviewId) {
        try {
            const response = await api.get(`/api/performance/admin/activity-log/${reviewId}`);
            return response.data.activities || [];
        } catch (error) {
            console.error('Error fetching activity log:', error);
            throw error;
        }
    }

    /**
     * Get productivity trends (Issue #16)
     */
    async getProductivityTrends(period = '6m', employeeId = null) {
        try {
            const params = new URLSearchParams();
            params.append('period', period);
            if (employeeId) {
                params.append('employee_id', employeeId);
            }
            const response = await api.get(`/api/performance/trends/productivity?${params.toString()}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching productivity trends:', error);
            throw error;
        }
    }
}

export default new PerformanceService();
